import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { FaPlus } from 'react-icons/fa';

interface PressRelease {
  title: string;
  dateline: string;
  content: string;
  contactInfo: string;
}

interface Quote {
  person: string;
  title: string;
  quote: string;
}

const PressReleaseGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Example data for quick-fill
  const exampleData = {
    companyName: 'Acme Corporation',
    city: 'San Francisco',
    state: 'CA',
    topic: 'Acme Launches Next-Gen AI Platform',
    industry: 'Technology',
    keyDetails: 'Acme Corporation has announced the launch of its next-generation AI platform, designed to help businesses automate workflows and gain deeper insights from their data.',
    boilerplate: 'About Acme Corporation: Acme is a leading provider of innovative technology solutions, empowering businesses worldwide.',
    quotes: [
      { person: 'Jane Doe', title: 'CEO', quote: 'This launch marks a major milestone for Acme and our clients.' }
    ],
    contactName: 'Jane Doe',
    contactTitle: 'CEO',
    contactEmail: 'jane.doe@acmecorp.com',
    contactPhone: '555-123-4567'
  };

  // Core press release details
  const [companyName, setCompanyName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [topic, setTopic] = useState('');
  const [industry, setIndustry] = useState('');
  const [keyDetails, setKeyDetails] = useState('');
  const [boilerplate, setBoilerplate] = useState('');
  
  // Contact information
  const [contactName, setContactName] = useState('');
  const [contactTitle, setContactTitle] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  
  // Quotes management
  const [quotes, setQuotes] = useState<Quote[]>([
    { person: '', title: '', quote: '' }
  ]);

  const handleUseExample = () => {
    setCompanyName(exampleData.companyName);
    setCity(exampleData.city);
    setState(exampleData.state);
    setTopic(exampleData.topic);
    setIndustry(exampleData.industry);
    setKeyDetails(exampleData.keyDetails);
    setBoilerplate(exampleData.boilerplate);
    setQuotes(exampleData.quotes);
    setContactName(exampleData.contactName);
    setContactTitle(exampleData.contactTitle);
    setContactEmail(exampleData.contactEmail);
    setContactPhone(exampleData.contactPhone);
  };
  
  // Generated press release
  const [pressRelease, setPressRelease] = useState<PressRelease | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const addQuote = () => {
    setQuotes([...quotes, { person: '', title: '', quote: '' }]);
  };

  const removeQuote = (index: number) => {
    if (quotes.length > 1) {
      const newQuotes = [...quotes];
      newQuotes.splice(index, 1);
      setQuotes(newQuotes);
    } else {
      toast.error('You must have at least one quote');
    }
  };

  const updateQuote = (index: number, field: keyof Quote, value: string) => {
    const newQuotes = [...quotes];
    newQuotes[index][field] = value;
    setQuotes(newQuotes);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!companyName || !topic || !contactName || !contactEmail) {
      toast.error('Please fill in all required fields: Company, Headline, Contact Name, Contact Email');
      return;
    }

    // Check if any quotes are missing information
    const hasIncompleteQuotes = quotes.some(q => !q.person || !q.title || !q.quote);
    if (hasIncompleteQuotes) {
      toast.error('Please complete all quote information or remove incomplete quotes');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating your AP-style press release...');

      // Get user ID from Supabase
      const userId = await getToken();
      
      if (!userId) {
        toast.error('Authentication error. Please log in again.');
        navigate('/login');
        return;
      }

      // Call the backend endpoint using the direct hardcoded approach that works in other components
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/press-release`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            companyName,
            city,
            state,
            topic,
            industry,
            keyDetails,
            boilerplate,
            quotes: quotes,
            contactName,
            contactTitle,
            contactEmail,
            contactPhone,
            format: 'AP' // Request AP format specifically
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate press release');
      }

      const data = await response.json();
      toast.dismiss();
      setPressRelease(data.pressRelease);
      toast.success('Press release generated successfully!');
    } catch (error) {
      toast.dismiss();
      console.error('Error generating press release:', error);
      toast.error(error instanceof Error ? error.message : 'An error occurred generating your press release');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (pressRelease) {
      const fullContent = `${pressRelease.title}\n\n${pressRelease.dateline}\n\n${pressRelease.content}\n\n${pressRelease.contactInfo}`;
      navigator.clipboard.writeText(fullContent);
      toast.success('Copied to clipboard!');
    }
  };

  // Handle case where user is not logged in
  if (!user) {
    return <div className="p-4 text-center">Please log in to access this feature.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-6xl relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-purple-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-8">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Press Release Generator</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Create professional AP-style press releases for company announcements
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left column - Input form */}
        <div className="w-full lg:w-1/2 relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Press Release Details</h2>
              <button
                type="button"
                className="bg-white/40 backdrop-blur-sm text-indigo-700 border border-white/40 text-xs py-1.5 px-3 rounded-lg shadow-sm hover:shadow transition-all duration-200"
                onClick={handleUseExample}
              >
                Use Example
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company details section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="companyName">
                  Company Name <span className="text-blue-500">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Corporation"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="industry">
                  Industry
                </label>
                <input
                  type="text"
                  id="industry"
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="Technology, Healthcare, etc."
                />
              </div>
            </div>

            {/* Dateline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="city">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="San Francisco"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="state">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="CA"
                />
              </div>
            </div>

            {/* Topic */}
            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="topic">
                Headline/Topic <span className="text-blue-500">*</span>
              </label>
              <input
                type="text"
                id="topic"
                className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Product Launch, Funding Announcement, etc."
                required
              />
            </div>

            {/* Key details */}
            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="keyDetails">
                Key Details
              </label>
              <textarea
                id="keyDetails"
                className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 h-24"
                value={keyDetails}
                onChange={(e) => setKeyDetails(e.target.value)}
                placeholder="Key points, facts, and figures about the announcement"
              />
            </div>

            {/* Quotes section */}
            <div className="border-t border-white/20 pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-md font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Quotes</h3>
                <button 
                  type="button" 
                  onClick={addQuote}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs py-1.5 px-3 rounded-lg shadow-sm hover:shadow transition-all duration-200 flex items-center space-x-1"
                >
                  <FaPlus size={10} />
                  <span>Add Quote</span>
                </button>
              </div>

              {quotes.map((quote, index) => (
                <div key={index} className="mb-4 p-3 backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-sm relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-purple-50/20 opacity-50"></div>
                  <div className="relative z-10">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/40 backdrop-blur-sm text-indigo-700 border border-white/40 shadow-sm">Quote {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeQuote(index)}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-white/40 backdrop-blur-sm text-red-500 hover:text-red-600 border border-white/40 shadow-sm transition-colors"
                      >
                        X
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <input
                          type="text"
                          className="w-full px-3 py-2 bg-white/30 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                          value={quote.person}
                          onChange={(e) => updateQuote(index, 'person', e.target.value)}
                          placeholder="Person Name"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          className="w-full px-3 py-2 bg-white/30 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                          value={quote.title}
                          onChange={(e) => updateQuote(index, 'title', e.target.value)}
                          placeholder="Title/Position"
                        />
                      </div>
                    </div>
                    <textarea
                      className="w-full px-3 py-2 bg-white/30 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 h-20"
                      value={quote.quote}
                      onChange={(e) => updateQuote(index, 'quote', e.target.value)}
                      placeholder="Quote text"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Company boilerplate */}
            <div>
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="boilerplate">
                Company Boilerplate
              </label>
              <textarea
                id="boilerplate"
                className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 h-24"
                value={boilerplate}
                onChange={(e) => setBoilerplate(e.target.value)}
                placeholder="Standard description of your company (About section)"
              />
            </div>

            {/* Contact information */}
            <div className="border-t border-white/20 pt-4">
              <h3 className="text-md font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="contactName">
                    Contact Name <span className="text-blue-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="contactTitle">
                    Contact Title
                  </label>
                  <input
                    type="text"
                    id="contactTitle"
                    className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                    value={contactTitle}
                    onChange={(e) => setContactTitle(e.target.value)}
                    placeholder="PR Manager"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="contactEmail">
                    Contact Email <span className="text-blue-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2" htmlFor="contactPhone">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full py-3 px-6 border border-transparent rounded-xl shadow-lg text-white font-medium text-base bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 transform hover:scale-[1.02]"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </div>
                ) : (
                  'Generate Press Release'
                )}
              </button>
            </div>
          </form>
          </div>
        </div>

        {/* Right column - Generated press release */}
        <div className="w-full lg:w-1/2 relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Generated Press Release</h2>
              {pressRelease && (
                <button
                  onClick={copyToClipboard}
                  className="bg-white/40 backdrop-blur-sm text-indigo-700 border border-white/40 text-xs py-1.5 px-3 rounded-lg shadow-sm hover:shadow transition-all duration-200 flex items-center space-x-1"
                  title="Copy to clipboard"
                >
                  <span>Copy</span>
                </button>
              )}
            </div>

          {pressRelease ? (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-sm overflow-auto max-h-[65vh]">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-purple-50/20 opacity-50"></div>
              <div className="relative z-10 p-5">
                <h3 className="text-xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-purple-700">{pressRelease.title}</h3>
                <p className="text-sm text-indigo-600 mb-4 font-medium">{pressRelease.dateline}</p>
                <div className="whitespace-pre-line text-gray-800 mb-5 bg-white/30 backdrop-blur-sm rounded-lg p-4 border border-white/30">{pressRelease.content}</div>
                <div className="text-sm text-gray-700 border-t border-indigo-100/50 pt-4 mt-2 bg-white/20 backdrop-blur-sm rounded-lg p-3">{pressRelease.contactInfo}</div>
              </div>
            </div>
          ) : (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 text-left mx-auto overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-purple-700">Example Press Release Output</h3>
                
                <div className="mb-3 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Acme Launches Next-Gen AI Platform</div>
                
                <div className="mb-3 text-sm text-indigo-600 font-medium bg-white/20 backdrop-blur-sm inline-block px-3 py-1 rounded-full border border-white/30">
                  SAN FRANCISCO, CA – April 21, 2025
                </div>
                
                <div className="mb-4 text-gray-800 bg-white/30 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                  Acme Corporation has announced the launch of its next-generation AI platform, designed to help businesses automate workflows and gain deeper insights from their data. <span className="italic text-indigo-700">"This launch marks a major milestone for Acme and our clients,"</span> said Jane Doe, CEO. The platform leverages advanced algorithms to streamline operations and deliver actionable analytics in real time.
                </div>
                
                <div className="mb-4 text-gray-800 bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <span className="font-medium">About Acme Corporation:</span> Acme is a leading provider of innovative technology solutions, empowering businesses worldwide.
                </div>
                
                <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-sm text-gray-700">
                  <p className="font-medium mb-1">Contact Information:</p>
                  <p>Jane Doe, CEO<br/>
                  Email: jane.doe@acmecorp.com<br/>
                  Phone: 555-123-4567</p>
                </div>
                
                <p className="text-center mt-6 text-indigo-600 font-medium">Fill out the form and click "Generate Press Release" to create your own!</p>
              </div>
            </div>
          )}
          <p className="mt-4 bg-blue-50/50 backdrop-blur-sm border border-blue-100/50 rounded-lg p-3 text-xs text-blue-700">
            <strong>AP Style Guide:</strong> Associated Press style emphasizes clarity and conciseness. Press releases follow a specific format with a compelling headline, dateline, lead paragraph containing the most important information (who, what, when, where, why), supporting paragraphs with quotes, and company information.
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PressReleaseGenerator;
